import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import axios from '../../services/BaseService';
import { getActiveTargets } from '../../services/ExchangeService';
import './Dashboard.css';

/**
 * In-house candle chart (replaces the TradingView iframe widget, which loaded
 * from an external CDN and silently broke — empty panes for every symbol).
 *
 * Data: our own /market/klines proxy (same-origin, 15s server cache) — immune
 * to adblockers and CSP, zero external requests from the page.
 *
 * The killer feature the widget could never do: the BOT's own levels are drawn
 * on the chart — average entry (gold), sell target / trailing activation
 * (green), break-even (grey) and the risk-engine stop (red) — so the chart
 * shows the strategy, not just the market.
 *
 * props:
 * - symbol (e.g. "BTCUSDC")
 */

const API_URL = process.env.REACT_APP_API_URL;
const INTERVALS = ['15m', '1h', '4h', '1d'];
const KLINES_POLL_MS = 20000;
const TARGETS_POLL_MS = 30000;

const COLORS = {
    up: '#10B981', down: '#E11D48',
    text: '#9AA4B2', grid: 'rgba(42, 50, 63, 0.45)', border: '#2A323F',
    gold: '#F5C451', grey: '#6B7280', volume: 'rgba(59, 130, 246, 0.25)',
    ema9: '#3B82F6', sma30: '#FBA918'
};

// The same moving averages the engines actually decide with (marketAnalysis.js):
// EMA9 — the momentum pullback reference (exact match on the 1h interval);
// SMA30 — the trend gate "price ≥ SMA30×0.92" (exact match on the 1d interval).
function emaSeries(candles, period) {
    const k = 2 / (period + 1);
    let prev = null;
    const out = [];
    candles.forEach((c, i) => {
        prev = prev === null ? c.close : c.close * k + prev * (1 - k);
        if (i >= period - 1) out.push({ time: c.time, value: prev });
    });
    return out;
}

function smaSeries(candles, period) {
    const out = [];
    let sum = 0;
    candles.forEach((c, i) => {
        sum += c.close;
        if (i >= period) sum -= candles[i - period].close;
        if (i >= period - 1) out.push({ time: c.time, value: sum / period });
    });
    return out;
}

function CandleChart(props) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);        // { chart, candles, volume, priceLines: [] }
    const [interval, setInterval_] = useState('15m');
    const [last, setLast] = useState(null); // { close, changePct } for the header badge

    // ----- chart lifecycle: (re)create per symbol/interval, poll for updates -----
    useEffect(() => {
        if (!props.symbol || !containerRef.current) return undefined;

        const chart = createChart(containerRef.current, {
            autoSize: true,
            layout: { background: { color: 'transparent' }, textColor: COLORS.text, fontSize: 11 },
            grid: { vertLines: { color: COLORS.grid }, horzLines: { color: COLORS.grid } },
            crosshair: { mode: CrosshairMode.Normal },
            rightPriceScale: { borderColor: COLORS.border },
            timeScale: { borderColor: COLORS.border, timeVisible: interval !== '1d', secondsVisible: false }
        });
        const candles = chart.addCandlestickSeries({
            upColor: COLORS.up, downColor: COLORS.down,
            wickUpColor: COLORS.up, wickDownColor: COLORS.down,
            borderVisible: false,
            priceFormat: { type: 'price', precision: 8, minMove: 0.00000001 }
        });
        const volume = chart.addHistogramSeries({
            priceFormat: { type: 'volume' }, priceScaleId: '', color: COLORS.volume
        });
        chart.priceScale('').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
        // Indicator overlays — thin, no axis labels (the axis stays readable).
        const mkLine = (color) => chart.addLineSeries({
            color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
            crosshairMarkerVisible: false
        });
        const ema9 = mkLine(COLORS.ema9);
        const sma30 = mkLine(COLORS.sma30);
        const state = { chart, candles, volume, ema9, sma30, priceLines: [], disposed: false, fitted: false };
        chartRef.current = state;

        async function loadKlines() {
            try {
                const r = await axios.get(`${API_URL}/market/klines/${props.symbol}?interval=${interval}&limit=240`);
                if (state.disposed || !Array.isArray(r.data) || !r.data.length) return;
                // Trim price precision noise: derive decimals from the last close.
                const lastRow = r.data[r.data.length - 1];
                const decimals = Math.max(2, Math.min(8, (String(lastRow.close).split('.')[1] || '').length));
                state.candles.applyOptions({ priceFormat: { type: 'price', precision: decimals, minMove: 1 / Math.pow(10, decimals) } });
                state.candles.setData(r.data.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));
                state.volume.setData(r.data.map(c => ({
                    time: c.time, value: c.volume,
                    color: c.close >= c.open ? 'rgba(16, 185, 129, 0.35)' : 'rgba(225, 29, 72, 0.35)'
                })));
                state.ema9.setData(emaSeries(r.data, 9));
                state.sma30.setData(smaSeries(r.data, 30));
                if (!state.fitted) { state.chart.timeScale().fitContent(); state.fitted = true; }
                const first = r.data[0];
                setLast({
                    close: lastRow.close,
                    changePct: first.open > 0 ? ((lastRow.close - first.open) / first.open) * 100 : 0
                });
            } catch (e) { /* transient — keep the previous candles on screen */ }
        }

        // Bot levels for THIS symbol from active-targets (entry / target / break-even / stop).
        async function loadBotLevels() {
            try {
                const targets = await getActiveTargets();
                if (state.disposed || !Array.isArray(targets)) return;
                state.priceLines.forEach(pl => { try { state.candles.removePriceLine(pl); } catch (e) { /* gone */ } });
                state.priceLines = [];
                targets.filter(tg => tg.symbol === props.symbol).forEach(tg => {
                    const add = (price, color, style, title) => {
                        if (!(parseFloat(price) > 0)) return;
                        state.priceLines.push(state.candles.createPriceLine({
                            price: parseFloat(price), color, lineWidth: 1, lineStyle: style,
                            axisLabelVisible: true, title
                        }));
                    };
                    add(tg.avgEntry, COLORS.gold, LineStyle.Solid, `Entry (${tg.engine})`);
                    add(tg.targetSell, COLORS.up, LineStyle.Dashed, 'Target');
                    add(tg.breakEven, COLORS.grey, LineStyle.Dotted, 'Break-even');
                    add(tg.stopPrice, COLORS.down, LineStyle.Dashed, 'Stop');
                });
            } catch (e) { /* levels are decoration — never break the chart over them */ }
        }

        loadKlines();
        loadBotLevels();
        const kTimer = window.setInterval(loadKlines, KLINES_POLL_MS);
        const tTimer = window.setInterval(loadBotLevels, TARGETS_POLL_MS);

        return () => {
            state.disposed = true;
            window.clearInterval(kTimer);
            window.clearInterval(tTimer);
            try { chart.remove(); } catch (e) { /* already gone */ }
            chartRef.current = null;
        };
    }, [props.symbol, interval]);

    if (!props.symbol) return null;

    return (
        <div className="row">
            <div className="col-12 mb-4">
                <div className="card border-0 shadow">
                    <div className="card-body p-2">
                        <div className="d-flex align-items-center justify-content-between px-2 pt-1 pb-2">
                            <div className="d-flex align-items-center gap-2">
                                <strong>{props.symbol}</strong>
                                {last ? (
                                    <span className={`num small ${last.changePct >= 0 ? 'gh-pos' : 'gh-neg'}`}>
                                        {last.close} ({last.changePct >= 0 ? '+' : ''}{last.changePct.toFixed(2)}%)
                                    </span>
                                ) : null}
                            </div>
                            <span className="small text-muted d-none d-md-inline">
                                <span style={{ color: COLORS.ema9 }}>―</span> EMA9&nbsp;&nbsp;
                                <span style={{ color: COLORS.sma30 }}>―</span> SMA30
                            </span>
                            <div className="btn-group" role="group" aria-label="Chart interval">
                                {INTERVALS.map(iv => (
                                    <button
                                        key={iv}
                                        type="button"
                                        className={`btn btn-sm py-0 px-2 ${iv === interval ? 'btn-primary' : 'btn-outline-gray'}`}
                                        onClick={() => setInterval_(iv)}
                                    >
                                        {iv}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div ref={containerRef} className="gh-chart" style={{ width: '100%' }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CandleChart;
